import { createPinia } from "pinia";
import { createApp, defineAsyncComponent } from "vue";
import {
  BeckButton,
  BeckLink,
  BeckDropdown,
  BeckDropdownItem,
  BeckOptions,
  BeckInput,
  BeckModal,
  BeckDoubleBubble,
  BeckNavigation,
  BeckNavigationItem,
  BeckAccount,
  BeckSpinner,
  BeckLogo
} from "beck-ui-components-vue";
import "beck-ui-components-vue/style.css";
import BeckFooter from "./components/BeckFooter.vue";
import Toast, { PluginOptions, POSITION } from "vue-toastification";
import "vue-toastification/dist/index.css";
import router from "./router";
import "./middleware/authentication";
import { setupMiddlewareLanguage } from "./middleware/language";
import "@fortawesome/fontawesome-svg-core/styles.css";
import { faSquareFacebook, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import {
  faSquareRss,
  faWandMagicSparkles,
  faMagnifyingGlass,
  faPaintBrush,
  faBell,
  faLock,
  faArrowLeft,
  faHome,
  faUser,
  faAnglesLeft,
  faAnglesRight,
  faClipboardList,
  faListOl,
  faEnvelope,
  faBook,
  faBookOpen,
  faUserLock,
  faFileLines,
  faPalette,
  faEye,
  faDiceD20,
  faLanguage,
  faHouse,
  faCartShopping,
  faFolderOpen,
  faGear,
  faPhone,
  faGlobe,
  faSortUp,
  faSortDown,
  faPen,
  faTrash,
  faCircleQuestion,
  faChalkboardUser,
  faCaretDown,
  faCircleExclamation,
  faCircleCheck
} from "@fortawesome/free-solid-svg-icons";
import { dom, config, library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import "./style.scss";
import { i18n } from "./plugins/i18n";
import VueMatomo from "vue-matomo";
import "bootstrap";
import { usePortals, PortalInfo } from "./helper/PortalHelper";
import { usePortalStore } from "./store";

setupMiddlewareLanguage(i18n);
const App = defineAsyncComponent(() => import("./App.component.vue"));
const Main = defineAsyncComponent(() => import("./layouts/main.vue"));
const Main_Sidebar = defineAsyncComponent(() => import("./layouts/main-sidebar.vue"));
const Error = defineAsyncComponent(() => import("./layouts/error.vue"));
const pinia = createPinia();
const app = createApp(App);

const options: PluginOptions = {
  position: POSITION.TOP_RIGHT,
  timeout: 2500
};

config.autoAddCss = false;
library.add(
  faSquareFacebook,
  faLinkedin,
  faSquareRss,
  faWandMagicSparkles,
  faMagnifyingGlass,
  faPaintBrush,
  faBell,
  faLock,
  faArrowLeft,
  faHome,
  faUser,
  faAnglesLeft,
  faAnglesRight,
  faClipboardList,
  faListOl,
  faEnvelope,
  faBook,
  faBookOpen,
  faUserLock,
  faFileLines,
  faPalette,
  faEye,
  faDiceD20,
  faLanguage,
  faHouse,
  faCartShopping,
  faFolderOpen,
  faGear,
  faPhone,
  faGlobe,
  faSortUp,
  faSortDown,
  faPen,
  faTrash,
  faCircleQuestion,
  faChalkboardUser,
  faCaretDown,
  faCircleExclamation,
  faCircleCheck
);
dom.watch();

app.component("main-layout", Main);
app.component("main-sidebar-layout", Main_Sidebar);
app.component("error-layout", Error);
app.component("font-awesome-icon", FontAwesomeIcon);
app.component("BeckButton", BeckButton);
app.component("BeckDropdown", BeckDropdown);
app.component("BeckDropdownItem", BeckDropdownItem);
app.component("BeckOptions", BeckOptions);
app.component("BeckLink", BeckLink);
app.component("BeckInput", BeckInput);
app.component("BeckModal", BeckModal);
app.component("BeckDoubleBubble", BeckDoubleBubble);
app.component("BeckNavigation", BeckNavigation);
app.component("BeckFooter", BeckFooter);
app.component("BeckNavigationItem", BeckNavigationItem);
app.component("BeckAccount", BeckAccount);
app.component("BeckSpinner", BeckSpinner);
app.component("BeckLogo", BeckLogo);
app.use(router);
app.use(pinia);

app.use(Toast, options);

const selectedPortal: PortalInfo = usePortals();

app.use(VueMatomo, {
  host: "https://beck.matomo.cloud",
  siteId: selectedPortal.matomoSiteId
});

// Store portal info in Pinia
const portalStore = usePortalStore();
portalStore.setPortalInfo(selectedPortal);

app.use(i18n);

// Wait for the initial route to be resolved before mounting.
// This avoids an early layout swap (default layout -> route meta layout)
// on deep-link reloads.
router.isReady().then(() => {
  app.mount("#app");
});
